import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../app/hooks';
import { clearComplianceItem } from '../state';
import { useGetComplianceItemByIdQuery } from '../../../services/ComplianceService/complianceApi';
import { PageContainer, PageCard } from '../../common/components/styled-ui-elements';
import { ComplianceEditHeader, ComplianceEditForm } from '../components/ComplianceForm';
import { ComplianceLoadingState, ComplianceErrorState } from '../components/shared';

/**
 * Resolves the message/details ComplianceErrorState should show for a failed or missing lookup.
 * Extracted from the page component so its own branching (loading/error/id-missing) stays flat.
 */
const resolveErrorState = (complianceItemId: string, isError: boolean, error: unknown): { message: string; details?: string } => {
  if (!complianceItemId) {
    return { message: 'No compliance requirement ID provided. Please provide a valid ID in the URL.' };
  }

  const details = isError && error && 'message' in (error as object)
    ? (error as any).message
    : undefined;

  return { message: 'Failed to load compliance requirement. Please check the ID and try again.', details };
};

/**
 * ComplianceItemEdit - Main page component for editing existing compliance requirements
 * Loads compliance item data from API using query parameters and provides editing interface
 */
const ComplianceItemEdit: React.FC = () => {
  const [searchParams] = useSearchParams();
  const complianceItemId = searchParams.get('id') || '';
  const dispatch = useAppDispatch();

  const {
    isLoading: isLoadingComplianceItem,
    isError,
    error,
  } = useGetComplianceItemByIdQuery(complianceItemId, {
    skip: !complianceItemId,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (complianceItemId) {
      dispatch(clearComplianceItem());
    }
  }, [dispatch, complianceItemId]);

  if (isLoadingComplianceItem) {
    return <ComplianceLoadingState message="Loading compliance requirement details..." />;
  }

  if (isError || !complianceItemId) {
    const { message, details } = resolveErrorState(complianceItemId, isError, error);
    return <ComplianceErrorState message={message} details={details} />;
  }

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard>
          <ComplianceEditHeader />
          <ComplianceEditForm />
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default ComplianceItemEdit;
